import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Container, Typography, Box, Button, Chip, Stack, 
  CircularProgress, Divider, Paper, Grid, Card 
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LaunchIcon from '@mui/icons-material/Launch';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import { motion } from 'framer-motion';
import { useCase, useCases } from '../../hooks/useCases';
import { Link } from 'react-router-dom';

import EditableImage from '../../components/ui/EditableImage';

const CaseDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { data: caseStudy, isLoading, error } = useCase(slug, i18n.language);
  const { data: allCases } = useCases({ isPublished: true, language: i18n.language });

  const suggestedCases = allCases
    ?.filter(c => c.slug !== slug)
    ?.sort(() => 0.5 - Math.random())
    ?.slice(0, 2) || [];

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', bgcolor: '#0B0F14' }}>
        <CircularProgress sx={{ color: '#0FA3A3' }} />
      </Box>
    );
  }

  if (error || !caseStudy) {
    return (
      <Container sx={{ py: 10, textAlign: 'center', bgcolor: '#0B0F14', minHeight: '60vh' }}>
        <Typography variant="h5" color="error" sx={{ mb: 4 }}>
          {t('messages.error_loading_case', 'Error loading case study')}
        </Typography>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/services')} sx={{ color: 'white' }}>
          {t('actions.back', 'Back')}
        </Button>
      </Container>
    );
  }

  return (
    <Box sx={{ bgcolor: '#0B0F14', minHeight: '100vh', pt: 15, pb: 10 }}>
      <Container maxWidth="lg">
        <Button 
          startIcon={<ArrowBackIcon />} 
          onClick={() => navigate('/services')}
          sx={{ color: '#9CA3AF', mb: 4, '&:hover': { color: 'white' } }}
        >
          {t('actions.back_to_services', 'Back to Services')}
        </Button>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Box sx={{ mb: 6 }}>
            <Typography variant="overline" component="div" sx={{ color: '#0FA3A3', fontWeight: 700, letterSpacing: 2 }}>
              <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>
                {caseStudy.case_label}
              </ReactMarkdown>
            </Typography>
            <Typography variant="h1" component="div" sx={{ color: 'white', mt: 2, mb: 3, fontFamily: 'Philosopher', fontSize: { xs: '2.5rem', md: '4rem' } }}>
              <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>
                {caseStudy.title}
              </ReactMarkdown>
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="h6" component="div" sx={{ color: '#D4AF37', fontWeight: 600 }}>
                <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>
                  {caseStudy.client_name}
                </ReactMarkdown>
              </Typography>
              <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
              <Typography variant="body1" component="div" sx={{ color: '#9CA3AF' }}>
                <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>
                  {caseStudy.result}
                </ReactMarkdown>
              </Typography>
            </Stack>
          </Box>

          {caseStudy.cover_image_url && (
            <EditableImage 
              src={caseStudy.cover_image_url}
              alt={caseStudy.title}
              tableName="project_cases"
              recordId={caseStudy.id}
              imageField="cover_image_url"
              sx={{ 
                width: '100%', 
                height: { xs: '300px', md: '500px' }, 
                mb: 8,
                borderRadius: '32px',
                overflow: 'hidden',
                boxShadow: '0 20px 40px rgba(0,0,0,0.4)'
              }}
            />
          )}

          <Grid container spacing={8}>
            <Grid item xs={12} md={8}>
              <Box className="markdown-content prose prose-invert max-w-none" sx={{ 
                color: '#D1D5DB', 
                fontSize: '1.1rem', 
                lineHeight: 1.8,
                '& p': { mb: 3 },
                '& h1, & h2, & h3': { color: 'white', fontFamily: 'Philosopher', mt: 4, mb: 2 },
                '& ul, & ol': { pl: 4, mb: 3 },
                '& li': { mb: 1 }
              }}>
                <ReactMarkdown 
                  remarkPlugins={[remarkGfm, remarkBreaks]}
                  components={{
                    h1: ({node, ...props}) => <Typography variant="h3" sx={{ mt: 4, mb: 2, fontFamily: 'Philosopher', color: 'white' }} {...props} />,
                    h2: ({node, ...props}) => <Typography variant="h4" sx={{ mt: 4, mb: 2, fontFamily: 'Philosopher', color: 'white' }} {...props} />,
                    h3: ({node, ...props}) => <Typography variant="h5" sx={{ mt: 3, mb: 1, fontFamily: 'Philosopher', color: 'white' }} {...props} />,
                    p: ({node, ...props}) => <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8, color: '#9CA3AF' }} {...props} />,
                    li: ({node, ...props}) => <Box component="li" sx={{ mb: 1, color: '#9CA3AF' }} {...props} />,
                    strong: ({node, ...props}) => <Box component="strong" sx={{ color: '#D4AF37', fontWeight: 700 }} {...props} />,
                  }}
                >
                  {caseStudy.content}
                </ReactMarkdown>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 4, bgcolor: '#111827', borderRadius: '24px', border: '1px solid rgba(255,255,255,0.05)', position: 'sticky', top: 100 }}>
                <Typography variant="h6" sx={{ color: 'white', mb: 3, fontWeight: 700 }}>
                  {t("caseDetail.technologiesUsed")}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {caseStudy.technologies?.map(tech => (
                    <Chip 
                      key={tech.technology?.id} 
                      label={tech.technology?.name} 
                      sx={{ bgcolor: 'rgba(15, 163, 163, 0.1)', color: '#0FA3A3', borderColor: 'rgba(15, 163, 163, 0.2)', border: '1px solid' }} 
                    />
                  ))}
                </Box>
              </Paper>

              {suggestedCases.length > 0 && (
                <Box sx={{ mt: 6 }}>
                  <Typography variant="h6" sx={{ color: 'white', mb: 3, fontWeight: 700 }}>
                    {t("caseDetail.suggestedTitle")}
                  </Typography>
                  <Stack spacing={3}>
                    {suggestedCases.map((suggested) => (
                      <Card 
                        key={suggested.id}
                        sx={{ 
                          bgcolor: '#111827', 
                          borderRadius: '20px', 
                          border: '1px solid rgba(255,255,255,0.05)',
                          overflow: 'hidden',
                          transition: '0.3s',
                          '&:hover': { borderColor: '#D4AF37' }
                        }}
                      >
                        <Box sx={{ height: '140px', position: 'relative' }}>
                          <EditableImage 
                            src={suggested.cover_image_url || "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80"}
                            alt={suggested.title}
                            tableName="project_cases"
                            recordId={suggested.id}
                            imageField="cover_image_url"
                            sx={{ height: '100%', width: '100%' }}
                            initialObjectFit={suggested.object_fit || 'cover'}
                            initialObjectPosition={suggested.object_position || '50% 50%'}
                          />
                        </Box>
                        <Box sx={{ p: 3 }}>
                          <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 700, mb: 1, fontFamily: 'Philosopher' }}>
                            {suggested.title}
                          </Typography>
                          <Button 
                            endIcon={<LaunchIcon />}
                            component={Link}
                            to={`/services/${suggested.slug}`}
                            size="small"
                            sx={{ color: '#D4AF37', p: 0, fontWeight: 700, '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' } }}
                          >
                            {t("caseDetail.viewCase")}
                          </Button>
                        </Box>
                      </Card>
                    ))}
                  </Stack>
                </Box>
              )}
            </Grid>
          </Grid>

          {/* Footer CTA Section */}
          <Box sx={{ 
            mt: 15, 
            p: { xs: 6, md: 8 }, 
            background: 'linear-gradient(135deg, #1F2937 0%, #111827 100%)', 
            borderRadius: '32px', 
            textAlign: 'center', 
            border: '1px solid rgba(255,255,255,0.05)',
            boxShadow: '0 20px 40px rgba(0,0,0,0.3)'
          }}>
            <Typography variant="h4" sx={{ color: 'white', mb: 3, fontFamily: 'Philosopher', fontWeight: 700 }}>
              {t("caseDetail.footerCta.titlePrefix")}{" "}
              <span style={{ color: '#0FA3A3' }}>{t("caseDetail.footerCta.titleHighlight")}</span>
            </Typography>
            <Typography variant="body1" sx={{ color: '#9CA3AF', mb: 5, maxWidth: '700px', mx: 'auto', fontSize: '1.1rem', lineHeight: 1.6 }}>
              {t("caseDetail.footerCta.subtitle")}
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} justifyContent="center">
              <Button 
                variant="contained" 
                size="large"
                component="a"
                href="https://calendly.com/sanoualexandreism/30min"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ 
                  bgcolor: '#D4AF37', 
                  color: '#0B0F14', 
                  px: 6, 
                  py: 2, 
                  borderRadius: '12px', 
                  fontWeight: 800,
                  fontSize: '1.1rem',
                  textTransform: 'none',
                  boxShadow: '0 10px 20px rgba(212, 175, 55, 0.2)',
                  '&:hover': { 
                    bgcolor: '#B8962E',
                    transform: 'translateY(-3px)',
                    boxShadow: '0 15px 30px rgba(212, 175, 55, 0.3)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                {t("caseDetail.footerCta.primaryButton")}
              </Button>
              <Button 
                variant="outlined" 
                size="large"
                component={Link}
                to="/contact"
                sx={{ 
                  borderColor: 'rgba(255,255,255,0.1)', 
                  color: 'white', 
                  px: 6, 
                  py: 2, 
                  borderRadius: '12px',
                  fontWeight: 800,
                  fontSize: '1.1rem',
                  textTransform: 'none',
                  '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.05)', transform: 'translateY(-3px)' },
                  transition: 'all 0.3s ease'
                }}
              >
                {t("caseDetail.footerCta.secondaryButton")}
              </Button>
            </Stack>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default CaseDetail;
